import { useState, useEffect, useCallback, useMemo } from 'react';

const MONTHS = [
  { value: '01', label: 'Januari' },
  { value: '02', label: 'Februari' },
  { value: '03', label: 'Mars' },
  { value: '04', label: 'April' },
  { value: '05', label: 'Maj' },
  { value: '06', label: 'Juni' },
  { value: '07', label: 'Juli' },
  { value: '08', label: 'Augusti' },
  { value: '09', label: 'September' },
  { value: '10', label: 'Oktober' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

function daysInMonth(month, year) {
  if (!month || !year) return 31;
  return new Date(parseInt(year), parseInt(month), 0).getDate();
}

/**
 * BirthDatePicker — 3 dropdowns for day/month/year.
 * Props:
 *   value: ISO string "YYYY-MM-DD" or ""
 *   onChange(isoString): called with "YYYY-MM-DD" when all 3 are set, or "" if incomplete
 *   error: boolean — show error styling
 *   className: extra wrapper class
 */
export function BirthDatePicker({ value = '', onChange, error = false, className = '' }) {
  const parsed = useMemo(() => {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return { y: '', m: '', d: '' };
    const [y, m, d] = value.split('-');
    return { y, m, d };
  }, [value]);

  const [year, setYear] = useState(parsed.y);
  const [month, setMonth] = useState(parsed.m);
  const [day, setDay] = useState(parsed.d);

  // Sync from prop when value changes externally
  useEffect(() => {
    setYear(parsed.y);
    setMonth(parsed.m);
    setDay(parsed.d);
  }, [parsed.y, parsed.m, parsed.d]);

  const currentYear = new Date().getFullYear();
  const years = useMemo(() => {
    const arr = [];
    for (let y = currentYear; y >= 1950; y--) arr.push(String(y));
    return arr;
  }, [currentYear]);

  const maxDay = daysInMonth(month, year);
  const days = useMemo(() => {
    const arr = [];
    for (let d = 1; d <= maxDay; d++) arr.push(String(d).padStart(2, '0'));
    return arr;
  }, [maxDay]);

  const emit = useCallback((y, m, d) => {
    // Clamp day if month/year changed
    const max = daysInMonth(m, y);
    const clampedDay = d && parseInt(d) > max ? String(max).padStart(2, '0') : d;
    if (y && m && clampedDay) {
      onChange?.(`${y}-${m}-${clampedDay}`);
    } else {
      onChange?.('');
    }
    return clampedDay;
  }, [onChange]);

  const selectClass = (hasValue) =>
    `flex-1 min-w-0 h-10 rounded-lg border px-2 text-sm bg-[rgb(var(--tn-bg-0a0a0a))] outline-none appearance-none transition-colors ${
      error ? 'border-red-500/60 focus:border-red-500' :
      hasValue ? "border-[rgb(var(--tn-border-ffffff))]/15 text-[rgb(var(--tn-text-ffffff))] focus:border-[#d4af37]/60" :
      "border-[rgb(var(--tn-border-ffffff))]/10 text-[rgb(var(--tn-text-ffffff))]/40 focus:border-[#d4af37]/60"
    }`;

  return (
    <div className={`flex gap-2 ${className}`} data-testid="birth-date-picker">
      {/* Year — most important for quick selection */}
      <select
        value={year}
        onChange={(e) => {
          const y = e.target.value;
          setYear(y);
          const cd = emit(y, month, day);
          if (cd !== day) setDay(cd);
        }}
        className={selectClass(!!year)}
        data-testid="birth-year-select"
      >
        <option value="" disabled>År</option>
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>

      {/* Month */}
      <select
        value={month}
        onChange={(e) => {
          const m = e.target.value;
          setMonth(m);
          const cd = emit(year, m, day);
          if (cd !== day) setDay(cd);
        }}
        className={selectClass(!!month)}
        data-testid="birth-month-select"
      >
        <option value="" disabled>Månad</option>
        {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
      </select>

      {/* Day */}
      <select
        value={day}
        onChange={(e) => {
          const d = e.target.value;
          setDay(d);
          emit(year, month, d);
        }}
        className={selectClass(!!day)}
        data-testid="birth-day-select"
      >
        <option value="" disabled>Dag</option>
        {days.map(d => <option key={d} value={d}>{parseInt(d)}</option>)}
      </select>
    </div>
  );
}

export default BirthDatePicker;
